import {
  collectDesignRefs,
  type DesignBody,
  type DesignNode,
  type FaceKey,
  type QuoteDesignViewFont,
  type QuoteDesignViewInk,
  type ResolvedEditAreaDefinition,
} from '@repo/api-contracts';
import type {
  CreateContext,
  FacePreviewInput,
  FacePreviewResult,
  MeasureText,
  RenderMaterials,
} from '@repo/design-renderer';
import type { Raster } from '@repo/warp-engine';
import { ImageLoadError } from '../load-image-raster';
import type { RasterCache } from '../materials-cache';

/**
 * 못 불러온 폰트의 대체 글꼴(V9) — 앱 기본 글꼴 Pretendard. 두 앱이 @repo/ui 테마(theme.css)로 싣는 웹 글꼴의 @font-face 이름이다.
 * worker 처럼 'sans-serif' 를 쓰지 않는 까닭: 렌더러 fontString 이 이름을 따옴표로 감싸 브라우저는 일반 글꼴로 읽지 않는다.
 * 이 글꼴이 없으면 브라우저 기본 글꼴로 그려진다 — 글자는 그대로 그리고 FONT_FALLBACK 을 알린다.
 */
export const VIEWER_FALLBACK_FONT_FAMILY = 'Pretendard Variable';

/** 뷰어 안 폰트 이름 — 지운 폰트와 살아 있는 폰트의 이름이 같을 수 있어 id 로 등록한다(V9). */
export const fontAlias = (fontId: string): string => `q-${fontId}`;

export type FaceRenderNotice = 'FONT_FALLBACK' | 'IMAGE_MISSING';
export type FontRegistration = 'loaded' | 'failed';
export type RegisterFont = (input: { family: string; url: string }) => Promise<FontRegistration>;

export interface FontRegistry {
  readonly ensure: (fontId: string, url: string) => Promise<FontRegistration>;
}

/**
 * 폰트 파일·대체 글꼴을 기다리는 상한(ms). 끝나지 않는 폰트 하나가 그 폰트를 쓰는 모든 면(페이지에 하나뿐인 등록부를 함께 쓴다)을
 * 영원히 '그리는 중'에 붙잡지 않도록 이 시간이 지나면 '폰트를 못 불러옴'으로 본다(V9) — 대체 글꼴로 그리고 FONT_FALLBACK 을 알린다.
 * 이미지에는 걸지 않는다(loadImageRaster 는 편집기와 함께 쓴다).
 */
export const FONT_LOAD_TIMEOUT_MS = 10_000;

/** work 가 ms 안에 끝나면 그 결과를, 아니면 onTimeout 을 준다. 먼저 끝난 쪽이 타이머를 치우고, 늦게 끝난 work 는 무시한다. */
const withTimeout = <T>(work: Promise<T>, ms: number, onTimeout: T): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => resolve(onTimeout), ms);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

/**
 * fontId 마다 한 번만 등록한다. 진행 중인 등록은 함께 쓴다. 실패했거나 FONT_LOAD_TIMEOUT_MS 안에 끝나지 않은 등록은
 * 지워 다음 그리기(다시 연 창)에서 다시 받는다. 상한 뒤에 늦게 성공해도 이미 낸 결과('failed')는 바뀌지 않는다.
 * 창을 다시 열어도 같은 FontFace 가 document.fonts 에 쌓이지 않도록 use-face-render 가 모듈 하나를 함께 쓴다.
 */
export const createFontRegistry = (register: RegisterFont): FontRegistry => {
  const entries = new Map<string, Promise<FontRegistration>>();
  return {
    ensure: (fontId, url) => {
      const cached = entries.get(fontId);
      if (cached !== undefined) return cached;
      const pending = withTimeout<FontRegistration>(
        register({ family: fontAlias(fontId), url }),
        FONT_LOAD_TIMEOUT_MS,
        'failed',
      )
        .catch((): FontRegistration => 'failed')
        .then((outcome) => {
          if (outcome === 'failed') entries.delete(fontId);
          return outcome;
        });
      entries.set(fontId, pending);
      return pending;
    },
  };
};

/** 그릴 때 읽는 뷰 데이터 — 만료 뒤 새 주소를 집도록 부를 때마다 최신 값을 준다. */
export interface FaceSources {
  readonly definition: ResolvedEditAreaDefinition;
  readonly body: DesignBody;
  readonly assetUrls: ReadonlyMap<string, string>;
  readonly fonts: readonly QuoteDesignViewFont[];
  readonly inks: readonly QuoteDesignViewInk[];
}

export interface RenderFaceInput {
  readonly faceKey: FaceKey;
  readonly colorOptionId: string;
  readonly maxPixels: number;
  readonly sources: () => FaceSources;
  readonly refreshUrls: () => Promise<void>;
  readonly rasterCache: RasterCache;
  readonly fontRegistry: FontRegistry;
  /** 대체 글꼴로 그릴 글자를 받아 앱 기본 글꼴을 미리 불러온다. 실패해도 그리기는 계속한다(최선 노력). */
  readonly loadFallbackFont: (text: string) => Promise<void>;
  readonly render: (input: FacePreviewInput) => FacePreviewResult;
  readonly createContext: CreateContext;
  readonly measure: MeasureText;
}

export interface RenderedFace {
  readonly raster: Raster;
  readonly notices: readonly FaceRenderNotice[];
}

const NOTICE_ORDER: readonly FaceRenderNotice[] = ['FONT_FALLBACK', 'IMAGE_MISSING'];
/** 다시 받기가 끝난 뒤 새 주소가 sources 에 들어올 때까지 더 기다리는 매크로태스크 수(다시 받기는 그대로 한 번). */
const LATE_SOURCE_TICKS = 3;

const nextTick = (): Promise<void> => new Promise((resolve) => setTimeout(resolve, 0));

/** changed() 가 참이 되거나 ticks 번 양보할 때까지 기다린다. */
const waitUntil = async (changed: () => boolean, ticks: number): Promise<void> => {
  if (ticks <= 0 || changed()) return;
  await nextTick();
  await waitUntil(changed, ticks - 1);
};

const isExpired = (error: unknown): boolean =>
  error instanceof ImageLoadError && error.kind === 'expired';

const facePhotoOf = (sources: FaceSources, colorOptionId: string, faceKey: FaceKey) =>
  sources.definition.colors.find((color) => color.colorOptionId === colorOptionId)?.faces[faceKey];

/**
 * 그 면에 놓인 컴포넌트의 본문만 — 다른 면의 이미지·폰트는 받지 않는다(V8).
 */
const faceBodyOf = (sources: FaceSources, faceKey: FaceKey): DesignBody => {
  const face = sources.definition.faces[faceKey];
  const placed = new Set((face?.placements ?? []).map((placement) => placement.componentId));
  return {
    components: sources.body.components.filter((component) => placed.has(component.componentId)),
  };
};

/** 그 폰트들로 쓰인 글자(그룹 안까지) — 대체 글꼴의 유니코드 범위 조각을 고르는 데 쓴다. */
const textsInFonts = (nodes: readonly DesignNode[], fontIds: ReadonlySet<string>): string[] =>
  nodes.flatMap((node) => {
    if (node.type === 'group') return textsInFonts(node.children, fontIds);
    return node.type === 'text' && fontIds.has(node.fontId) ? [node.text] : [];
  });

/**
 * 고정 사본의 면 하나를 그린다(V8). 사진·마스크·음영·이 면의 고객 이미지·폰트를 모아 renderFacePreview 를 부른다.
 * 만료(403)는 이 렌더에서 뷰 데이터를 한 번만 다시 받아 새 주소로 다시 받고, 또 실패하면 던진다(V12).
 * 고객 이미지는 실패해도 빼고 그리고(IMAGE_MISSING), 폰트는 앱 기본 글꼴(Pretendard)로 그린다(FONT_FALLBACK, V9).
 */
export const renderFace = async (input: RenderFaceInput): Promise<RenderedFace> => {
  const { faceKey, colorOptionId, rasterCache } = input;
  let refreshing: Promise<void> | undefined;
  // 함께 만료된 재료는 같은 다시 받기를 기다린다. 받은 뒤 구독자 재렌더가 새 주소를 담도록 매크로태스크 한 번을 양보한다.
  const refreshOnce = (): Promise<void> => {
    refreshing ??= input.refreshUrls().then(nextTick);
    return refreshing;
  };
  const load = async (
    key: string,
    urlOf: (sources: FaceSources) => string | undefined,
  ): Promise<Raster> => {
    const url = urlOf(input.sources());
    if (url === undefined) throw new RangeError(`재료 주소가 없다: ${key}`);
    try {
      return await rasterCache.get(key, url);
    } catch (error) {
      if (!isExpired(error)) throw error;
      await refreshOnce();
      // 재렌더가 늦으면 새 주소가 아직 없다 — 다시 받지 않고 몇 틱 더 기다린다.
      await waitUntil(() => urlOf(input.sources()) !== url, LATE_SOURCE_TICKS);
      rasterCache.invalidate(key);
      return rasterCache.get(key, urlOf(input.sources()) ?? url);
    }
  };

  const start = input.sources();
  const photoEntry = facePhotoOf(start, colorOptionId, faceKey);
  if (photoEntry === undefined)
    throw new RangeError(`색상 ${colorOptionId} 에 ${faceKey} 면 사진이 없다`);
  const composite = start.definition.faces[faceKey]?.composite;
  const faceBody = faceBodyOf(start, faceKey);
  const refs = collectDesignRefs(faceBody);
  const notices = new Set<FaceRenderNotice>();
  const fallbackFontIds = new Set<string>();

  const faceRasters = Promise.all([
    load(photoEntry.photoKey, (sources) => facePhotoOf(sources, colorOptionId, faceKey)?.photoUrl),
    composite?.maskKey === undefined
      ? undefined
      : load(composite.maskKey, (sources) => sources.definition.faces[faceKey]?.composite?.maskUrl),
    composite?.shadingKey === undefined
      ? undefined
      : load(
          composite.shadingKey,
          (sources) => sources.definition.faces[faceKey]?.composite?.shadingUrl,
        ),
  ]);
  const images = new Map<string, Raster>();
  const faceImages = Promise.all(
    [...refs.assetIds].map(async (assetId) => {
      try {
        images.set(assetId, await load(assetId, (sources) => sources.assetUrls.get(assetId)));
      } catch {
        // 고객 이미지 하나 때문에 면 전체를 버리지 않는다 — 그 이미지만 빼고 그린다(V12).
        notices.add('IMAGE_MISSING');
      }
    }),
  );
  const fontsById = new Map(start.fonts.map((font) => [font.id, font] as const));
  const faceFonts = Promise.all(
    [...refs.fontIds].map(async (fontId) => {
      const font = fontsById.get(fontId);
      const status =
        font === undefined ? 'failed' : await input.fontRegistry.ensure(fontId, font.fileUrl);
      if (status === 'failed') {
        notices.add('FONT_FALLBACK');
        fallbackFontIds.add(fontId);
      }
      // 글자를 빼지 않는다 — 못 불러온 폰트는 앱 기본 글꼴로 그린다(V9).
      const familyName = status === 'loaded' ? fontAlias(fontId) : VIEWER_FALLBACK_FONT_FAMILY;
      return [fontId, { familyName }] as const;
    }),
  );
  // 이미지 실패는 안내로 바뀌어 던지지 않는다 — 사진·마스크·음영 실패만 이 면을 실패로 만든다.
  const [[photo, mask, shading], fonts] = await Promise.all([faceRasters, faceFonts, faceImages]);
  if (fallbackFontIds.size > 0) {
    const text = faceBody.components
      .flatMap((component) => textsInFonts(component.layers, fallbackFontIds))
      .join('');
    try {
      await withTimeout(input.loadFallbackFont(text), FONT_LOAD_TIMEOUT_MS, undefined);
    } catch {
      // 최선 노력 — 앱 글꼴이 없거나 상한 안에 못 받으면 브라우저 기본 글꼴로 그린다. 글자와 FONT_FALLBACK 안내는 그대로다.
    }
  }

  const materials: RenderMaterials = {
    images,
    fonts: new Map(fonts),
    inks: new Map(start.inks.map((ink) => [ink.id, ink.colorCode] as const)),
  };
  const result = input.render({
    definition: start.definition,
    colorOptionId,
    faceKey,
    body: start.body,
    materials,
    face: {
      photo,
      ...(mask === undefined ? {} : { mask }),
      ...(shading === undefined ? {} : { shading }),
    },
    createContext: input.createContext,
    measure: input.measure,
    maxPixels: input.maxPixels,
  });
  return { raster: result.raster, notices: NOTICE_ORDER.filter((notice) => notices.has(notice)) };
};
